"use client";

import { getUser } from "@/actions";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useAppDispatch, useAppSelector } from "@/lib/store/hook";
import { setShareUrl, setSocialModal } from "@/lib/store/slices/application/application.slice";
import { iconpath } from "@/util/staticresourcespath";
import { copyToClipboard, copyToIOSClipboard, isEncoded } from "@/util/util";
import { Forward } from "lucide-react";
import { getSession } from "next-auth/react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
interface SocialMedia {
    id: string;
    name: string;
    iconUrl: string;
    linkUrl: string;
}

const socials: SocialMedia[] = [
    {
        id: "Facebook",
        name: "Facebook",
        iconUrl: "Facebook.png",
        linkUrl: "https://www.facebook.com/sharer.php?u={{currentPath}}",
    },
    {
        id: "Line",
        name: "Line",
        iconUrl: "LINE_Brand_icon.png",
        linkUrl: "http://line.me/R/msg/text/?{{currentPath}}",
    },
    {
        id: "X",
        name: "X",
        iconUrl: "X.png",
        linkUrl: "https://twitter.com/share?text=&url={{currentPath}}",
    },
    {
        id: "CopyLink",
        name: "Copy Link",
        iconUrl: "copy-link.png",
        linkUrl: "{{currentPath}}",
    },
];

export function ShareSocialModal() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [currentPath, setCurrentPath] = useState("");
    const [customerAccount, setCustomerAccount] = useState<string | null>();
    const [canShare, setCanShare] = useState(false);

    const { isSocialModelOpen, shareUrl } = useAppSelector((state) => state.ApplicationReducer);

    
    const getUrlForPlatform = (platform: string) => {
        
        const baseUrl = shareUrl || (typeof window !== 'undefined' ? window.location.href : '');

        if (!baseUrl) {
            return ''; 
        }

        
        const params: string[] = [];

        
        if (platform !== "CopyLink") {
            params.push(`ref=${platform.toLowerCase()}`);
        }

        
        if (customerAccount) {
            params.push(`cid=${customerAccount}`);
        }

        
        params.push("channel=DTC_sharelink");

        const queryString = params.join("&");

        
        const separator = baseUrl.includes('?') ? '&' : '?';
        const fullUrl = `${baseUrl}${separator}${queryString}`;

        return isEncoded(fullUrl) ? fullUrl : encodeURI(fullUrl);
    };

    useEffect(() => {
        
        setCurrentPath(getUrlForPlatform("CopyLink"));

        if (typeof window !== 'undefined' && typeof navigator !== 'undefined' && 'share' in navigator) {
            console.log('Web Share API is available');
            setCanShare(true);
        } else {
            console.log('Web Share API is NOT available');
        }
    }, [customerAccount, shareUrl]);

    useEffect(() => {
        const getCustomerAccount = async () => {
            const session = await getSession();
            if (session) {
                const user = await getUser();
                if (user && user.level !== "GUEST" && user.level !== "NORMAL") {
                    setCustomerAccount(user.customerAccount);
                }
            }
        };
        getCustomerAccount();
    }, []);

    const handleOnClick = async (id: string, linkUrl: string) => {
      
        if (id === "CopyLink") {
            try {
                await copyToClipboard(currentPath);
                toast.success("Copied to clipboard", { position: "bottom-center" });
            } catch (err) {
                try {
                    await copyToIOSClipboard(currentPath);
                    toast.success("Copied to clipboard", { position: "bottom-center" });
                } catch (err: any) {
                    toast.error("(4) Failed to copy: " + err.message, { position: "bottom-center" });
                }
            }
            return;
        }

        if (canShare && navigator.share) {
            const urlToShare = getUrlForPlatform(id);

            try {
                await navigator.share({
                    title: document.title || 'Share',
                    url: urlToShare,
                });
                return;
            } catch (err: any) {
                if (err.name === 'AbortError') {
                    return;
                }
                console.error('Web Share API error:', err);
            }
        }

   
        const url = linkUrl.replace(
            "{{currentPath}}",
            encodeURIComponent(getUrlForPlatform(id))
        );
        window.open(url, '_blank');
    };

    return (
        <>
            <Dialog
                open={isSocialModelOpen}
                onOpenChange={(open) => {
                    dispatch(setSocialModal(false));
                    if (!open) {
                        dispatch(setShareUrl(null));
                    }
                }}
            >
                <DialogContent className="flex p-0 border-none sm:max-w-[400px] max-w-[300px]  bg-white">
                    <div
                        className="flex flex-col rounded-lg  shadow-md gap-4 px-6 py-4 w-full h-56"
                        style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}>
                        <div className="flex justify-between">
                            <div className="flex flex-row gap-2">
                                <Forward className="text-c2" />
                                <div className="font-font1 text-base">{t("share.btn_share")}</div>
                            </div>
                        </div>
                        <div className="font-normal text-base">
                            {t("share.recommend_business")}
                            <br />
                            {t("share.open_opportunities")}
                        </div>
                        <div className="flex flex-row gap-6">
                            {socials.map((socail: SocialMedia) =>
                                canShare || socail.id === "CopyLink" ? (
                                    <button
                                        key={socail.id}
                                        type="button"
                                        className="focus:outline-none active:opacity-70 transition-opacity"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleOnClick(socail.id, socail.linkUrl);
                                        }}>
                                        <div className="flex flex-col gap-2 text-center select-none">
                                            <Image
                                                src={iconpath + socail.iconUrl}
                                                alt={socail.name}
                                                width={48}
                                                height={48}
                                                className="self-center pointer-events-none"
                                                draggable={false}
                                                style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
                                            />
                                            <div className="font-light text-xs">{socail.name}</div>
                                        </div>
                                    </button>
                                ) : (
                                    <Link
                                        key={socail.id}
                                        href={socail.linkUrl.replace(
                                            "{{currentPath}}",
                                            encodeURIComponent(getUrlForPlatform(socail.id)),
                                        )}
                                        target="_blank"
                                        className="select-none"
                                        onContextMenu={(e) => e.preventDefault()}>
                                        <div className="flex flex-col gap-2 text-center">
                                            <Image
                                                src={iconpath + socail.iconUrl}
                                                alt={""}
                                                width={48}
                                                height={48}
                                                className="self-center pointer-events-none select-none"
                                                draggable={false}
                                                unoptimized
                                            />
                                            <div className="font-light text-xs select-none">{socail.name}</div>
                                        </div>
                                    </Link>
                                ),
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
